import {
  Box,
  Text,
  Stack,
  HStack,
  Badge,
  Image,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  ModalOverlay,
  Spacer,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import React from "react";
import { FaRegCreditCard } from "react-icons/fa";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

export function BonoActivity(props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("ca")
    ? "ca-ES"
    : i18n.language?.startsWith("en")
    ? "en-GB"
    : "es-ES";

  let statusColor;
  let statusVariant;
  let statusText;
  switch (props.bonoStatus) {
    case "ACCEPTED":
      statusColor = "blue"; // Green color for ACTIVO status
      statusVariant = "solid";
      statusText = t("activity.bonos.status.accepted");
      break;
    case "DRAINED":
      statusColor = "red"; // Green color for ACTIVO status
      statusVariant = "outline";
      statusText = t("activity.bonos.status.drained");
      break;
    case "CANCELLED":
      statusColor = "red"; // Red color for CANCELADO status
      statusVariant = "solid";
      statusText = t("activity.bonos.status.cancelled");
      break;
    case "REJECTED":
      statusColor = "red"; // Red color for CANCELADO status
      statusVariant = "solid";
      statusText = t("activity.bonos.status.rejected");
      break;
    case "EXPIRED":
      statusColor = "red"; // Orange color for EXPIRADO status
      statusVariant = "outline";
      statusText = t("activity.bonos.status.expired");
      break;
    case "INACTIVE":
      statusColor = "blue"; // Orange color for EXPIRADO status
      statusVariant = "outline";
      statusText = t("activity.bonos.status.inactive");
      break;
    case "OUTSTANDING":
      statusColor = "yellow"; // Blue color for PENDIENTE status
      statusVariant = "outline";
      statusText = t("activity.bonos.status.outstanding");
      break;
    default:
      statusColor = "#000000"; // Default black color for unknown status
      statusVariant = "solid";
  }

  const options = { year: "2-digit", month: "2-digit", day: "2-digit" };

  function formatDate(value) {
    if (value === undefined)
      return props.bonoName === "CUC Day"
        ? t("activity.bonos.durationDay")
        : t("activity.bonos.durationMonth");
    let date = new Date(value);
    return new Intl.DateTimeFormat(locale, options).format(date);
  }

  const {
    control,
    handleSubmit,
    register: eventRegister,
    setError,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      bonoId: props.bonoId,
      bonoType: props.bonoName,
      activationDate: props.bonoActivationDate,
      expirationDate: props.bonoExpirationDate,
    },
    shouldFocusError: false,
  });

  useEffect(() => {
    reset({
      bonoId: props.bonoId,
      activationDate: props.bonoActivationDate
        ? props.bonoActivationDate.split("T")[0]
        : null,
      expirationDate: props.bonoExpirationDate
        ? props.bonoExpirationDate.split("T")[0]
        : null,
      bonoType: props.bonoName,
    });
  }, [
    props.bonoId,
    props.bonoActivationDate,
    props.bonoExpirationDate,
    props.bonoName,
    reset,
  ]);

  async function onSubmit(values) {
    if (new Date(values.activationDate) > new Date(values.expirationDate)) {
      setError("activationDate", {
        type: "manual",
        message: t("BE-3"),
      });

      return;
    }

    setIsLoading(true);
    await axios
      .put("/api/admin/bono/update/" + values.bonoId, {
        activationDate: new Date(values.activationDate).toISOString(),
        expirationDate: new Date(values.expirationDate).toISOString(),
        remainingSessions: values.remainingSessions,
      })
      .then(() => {
        toast({
          title: t("activity.bonos.toastUpdated"),
          description: "",
          position: "bottom-right",
          duration: 4000,
          isClosable: true,
        });
        props.onAction(true);
        onClose(true);
      })
      .catch((error) => {
        console.log(error);
        toast({
          title: t("common.unexpectedErrorTitle"),
          description: t(error.response.data.code),
          status: "error",
          position: "bottom-right",
          duration: 4000,
          isClosable: true,
        });
      });
    setIsLoading(false);
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="sm"
        borderRadius={"0px"}
        returnFocusOnClose={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign={"left"} pb={0}>
            {t("activity.bonos.editTitle")}
          </ModalHeader>

          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody pb={0}>
              <Stack spacing={3} mb={0.5}>
                <BonoActivity
                  key={props.bonoId}
                  bonoId={props.bonoId}
                  bonoName={props.bonoName}
                  bonoImage={props.bonoImage}
                  bonoStatus={props.bonoStatus}
                  bonoSessions={props.bonoSessions}
                  bonoCreationDate={props.bonoCreationDate}
                  bonoActivationDate={props.bonoActivationDate}
                  bonoExpirationDate={props.bonoExpirationDate}
                  bonoEdit={false}
                />
                <HStack>
                  <FormControl>
                    <FormLabel htmlFor="bonoType" fontSize="sm">
                      {t("activity.bonos.fieldType")}
                    </FormLabel>
                    <Input
                      id="bonoType"
                      isReadOnly
                      type="text"
                      placeholder={t("activity.bonos.fieldTypePlaceholder")}
                      size="sm"
                      {...eventRegister("bonoType", {
                        required: t("auth.required"),
                      })}
                    />
                  </FormControl>
                  <FormControl isInvalid={errors.remainingSessions}>
                    <FormLabel htmlFor="remainingSessions" fontSize="sm">
                      {t("activity.bonos.fieldSessions")}
                    </FormLabel>
                    <Controller
                      name="remainingSessions"
                      control={control}
                      defaultValue={props.bonoSessions || 0} // Valor inicial si es necesario
                      rules={{
                        required: t("auth.required"),
                      }}
                      render={({ field }) => (
                        <NumberInput
                          {...field} // Pasa propiedades del campo al NumberInput
                          min={-1}
                          max={100}
                          size="sm"
                          onChange={(valueAsString, valueAsNumber) => {
                            field.onChange(valueAsNumber); // Actualiza el valor con el valor numérico
                          }}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      )}
                    />
                  </FormControl>
                </HStack>
                <HStack py={2} max-width="336px">
                  <Box width={"48.5%"}>
                    <FormControl isInvalid={errors.activationDate}>
                      <FormLabel htmlFor="activationDate" fontSize="sm">
                        {t("activity.bonos.fieldActivation")}
                      </FormLabel>
                      <Input
                        id="activationDate"
                        isDisabled={props.bonoStatus === "REJECTED"}
                        type="date"
                        placeholder={t("activity.bonos.fieldActivationPlaceholder")}
                        size="sm"
                        {...eventRegister("activationDate", {
                          required: t("auth.required"),
                        })}
                        max-width="164.01px"
                      />
                    </FormControl>
                  </Box>

                  <Box width={"49%"}>
                    <FormControl isInvalid={errors.expirationDate}>
                      <FormLabel htmlFor="expirationDate" fontSize="sm">
                        {t("activity.bonos.fieldExpiration")}{" "}
                      </FormLabel>
                      <Input
                        id="expirationDate"
                        isDisabled={props.bonoStatus === "REJECTED"}
                        type="date"
                        placeholder={t("activity.bonos.fieldExpirationPlaceholder")}
                        size="sm"
                        {...eventRegister("expirationDate", {
                          required: t("auth.required"),
                        })}
                      />
                    </FormControl>
                  </Box>
                </HStack>
                {errors.activationDate && (
                  <Text fontSize="xs" color="red.500" fontWeight={400} pb={2}>
                    {errors.activationDate.message}
                  </Text>
                )}
              </Stack>
            </ModalBody>
            <ModalFooter justifyContent="center">
              <Button onClick={onClose} isDisabled={isLoading}>
                {t("common.cancel")}
              </Button>
              <Spacer />
              <Button
                variant={"outline"}
                type="submit"
                colorScheme="blue"
                isDisabled={props.bonoStatus === "REJECTED"}
                isLoading={isLoading}
              >
                {t("activity.bonos.save")}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      <Box m="auto" w="100%" onClick={() => props.bonoEdit && onOpen(true)}>
        <HStack
          m="auto"
          role={"group"}
          p={4}
          bg="white"
          boxShadow={"sm"}
          spacing={5}
          w="100%"
          cursor={!props.bonoRequest ? "pointer" : "default"}
        >
          <Image
            className="bono-purchased-img"
            src={props.bonoImage}
            alt={props.bonoName + " logo"}
          />

          <Stack m="auto" ml={0} textAlign={"left"}>
            <Text
              fontFamily={"Oswald"}
              fontWeight="500"
              fontSize="2xl"
              color="black"
            >
              {props.bonoName}
            </Text>
            {!props.bonoRequest && (
              <HStack>
                <Badge
                  variant={statusVariant}
                  colorScheme={statusColor}
                  width={"fit-content"}
                  borderRadius={0}
                >
                  {statusText}
                </Badge>
                {props.bonoPaymentStatus === "OUTSTANDING" && (
                  <>
                    <Popover returnFocusOnClose={false} flip={false}>
                      <PopoverTrigger>
                        <Button
                          size="xl"
                          variant="ghost"
                          _hover={{
                            bg: "transparent",
                          }}
                        >
                          <FaRegCreditCard />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent color="black" width="fit-content">
                        <PopoverArrow />
                        <PopoverBody className="allow-select" cursor="auto">
                          <HStack>
                            <Text>
                              {t("activity.bonos.outstandingAmount")}:{" "}
                              <b>{props.bonoTotal.toFixed(2)}€</b>{" "}
                              {parseFloat(props.bonoDiscount) !== 0 &&
                                "(-" + props.bonoDiscount + "%)"}
                            </Text>
                          </HStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </>
                )}
              </HStack>
            )}
            <Stack fontWeight={400} fontSize={"sm"} spacing={-0.5}>
              {props.bonoRequest && (
                <Text>
                  {t("activity.bonos.price")}:
                  <b> {props.bonoPrice} €</b>
                </Text>
              )}
              <Text>
                {t("activity.bonos.sessions")}:{" "}
                <b>
                  {props.bonoSessions !== -1
                    ? props.bonoSessions
                    : t("activity.bonos.unlimited")}
                </b>
              </Text>
              <Text></Text>
              <Text>
                {props.bonoStatus === "REJECTED" ? (
                  <>
                    {t("activity.bonos.requestedAt")}: {" "}
                    <b>{formatDate(props.bonoCreationDate)}</b>
                  </>
                ) : props.bonoStatus === "INACTIVE" ? (
                  <>
                    {t("activity.bonos.activatedAt")}: {" "}
                    <b>{formatDate(props.bonoActivationDate)}</b>
                  </>
                ) : props.bonoStatus === "OUTSTANDING" ? (
                  <>
                    {t("activity.bonos.requestedAt")}: {" "}
                    <b>{formatDate(props.bonoCreationDate)}</b>
                  </>
                ) : (
                  <>
                    {t("activity.bonos.expiresAt")}: {" "}
                    <b>{formatDate(props.bonoExpirationDate)}</b>
                  </>
                )}
              </Text>
            </Stack>
          </Stack>
        </HStack>
      </Box>
    </>
  );
}
